import { useState } from "react";
import { Link } from "react-router-dom";
import { Cross, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(apiError(err, "Could not process the request. Please try again later."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy grain-overlay px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="forgot-logo">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-gold text-navy">
              <Cross className="h-6 w-6" strokeWidth={2.5} />
            </span>
          </Link>
          <h1 className="mt-5 font-heading text-2xl font-bold text-cream">Reset Your Password</h1>
          <p className="mt-1 text-sm text-cream/60">Christ Church CNI, Rajpura — Admin Panel</p>
        </div>
        <div className="rounded-lg border border-cream/10 bg-navy-light/60 p-8 backdrop-blur">
          {done ? (
            <div>
              <div className="rounded-md border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-cream" data-testid="forgot-password-success">
                If an account exists for this email address, a password reset link has been sent.
              </div>
              <p className="mt-4 text-center text-xs text-cream/50">
                The link expires in 30 minutes. Please check your inbox and spam folder.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              {error && (
                <div className="rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert" data-testid="forgot-password-error">
                  {error}
                </div>
              )}
              <div>
                <Label htmlFor="fp-email" className="text-cream/80">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  required
                  autoComplete="email"
                  data-testid="forgot-password-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 border-cream/20 bg-navy text-cream placeholder:text-cream/30"
                  placeholder="you@example.com"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-navy hover:bg-gold-dark hover:text-cream transition-colors"
                data-testid="forgot-password-submit"
              >
                <Mail className="mr-2 h-4 w-4" /> {loading ? "Sending…" : "Send Reset Link"}
              </Button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-cream/40">
          <Link to="/admin/login" className="hover:text-gold transition-colors" data-testid="back-to-login-link">← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
